import React from 'react'
import './access-policy-list.scss'
import { Button } from '@pushly/aqe/lib/components'
import type { ButtonAction } from '@pushly/aqe/lib/components'
import { classnames } from '@halo/_utils/classnames'
import { observer } from 'mobx-react'
import {
    Loading3QuartersOutlined,
    WarningOutlined,
    EyeOutlined,
    QuestionCircleOutlined,
    EyeInvisibleOutlined,
} from '@ant-design/icons'
import { Table, Input, Modal, Form, FormInstance, Flex } from 'antd'
import { AppService } from '@halo/services/app.service'
import { Container } from 'typescript-ioc/es5'
import { TablePaginationConfig } from 'antd/es/table'
import { TableRowEntityDisplay } from '@halo/components/table-row-entity-display/table-row-entity-display'
import { FilterValue, SorterResult, TableCurrentDataSource } from 'antd/es/table/interface'
import { RuleObject } from 'antd/es/form'
import { AppState } from '@halo/stores/app.state'
import AccessPolicyService from '@halo/services/access-policy.service'
import { IAccessPolicy } from '@halo/interfaces/access-policy'
import AccessPolicyExpandedRowView from '@halo/components/access-policy-expanded-row-view/access-policy-expanded-row-view'
import TableRowActiveBadge from '@halo/components/table-row-active-badge/table-row-active-badge'
import AccessPolicyEditor from '@halo/components/access-policy-view/access-policy-editor'
import { simpleNotification } from '@halo/_utils/notifications'
import { isAxiosResponseError } from '@halo/_utils/axios'
import SwSimpleLinkPagination from '@halo/components/sw-simple-link-pagination/sw-simple-link-pagination'
import { IApiResponsePaginationLinks } from '@halo/interfaces/api-response-pagination-links'

const DISABLE_KEY = 'DISABLE'

interface IListProps {}

interface IListState {
    loading: boolean
    policiesLoaded: boolean
    allPolicies: IAccessPolicy[]
    visiblePolicies: IAccessPolicy[]
    tableMeta: any
    sortedInfo: any
    page: number
    pageSize: number
    query?: string
    paginationLinks?: IApiResponsePaginationLinks

    newPolicyDrawerVisible: boolean
}

@observer
class AccessPolicyList extends React.Component<IListProps, IListState> {
    protected appState: AppState
    protected appService: AppService
    protected accessPolicyService: AccessPolicyService

    public constructor(props: IListProps) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.accessPolicyService = Container.get(AccessPolicyService)

        this.state = {
            loading: true,
            policiesLoaded: false,
            allPolicies: [],
            visiblePolicies: [],
            tableMeta: {},
            page: 1,
            pageSize: 15,
            sortedInfo: {
                order: 'ascend',
                columnKey: 'name',
            },
            newPolicyDrawerVisible: false,
        }
    }

    public componentDidMount(): void {
        this.ensureAcessPoliciesLoaded()
    }

    public async ensureAcessPoliciesLoaded() {
        await this.fetchAccessPolicies()
    }

    public render() {
        const tableHeader = (
            <Flex className="access-policy-list-header" gap="small" justify="space-between" align="center">
                <Flex flex={1} gap="small" align="center">
                    <Input.Search
                        className="access-policy-list-search"
                        style={{ width: 300 }}
                        size="middle"
                        placeholder="Search by ID or Policy Name"
                        enterButton={true}
                        onSearch={this.searchUserRoles}
                    />
                </Flex>
                <Flex align="center">
                    <Button size="small" type="primary" onClick={this.showNewPolicyDrawer}>
                        New Access Policy
                    </Button>
                </Flex>
            </Flex>
        )

        return (
            <>
                <div className={classnames('access-policy-list')}>
                    <Table<IAccessPolicy>
                        className="aqe-table-well"
                        title={() => tableHeader}
                        size="small"
                        bordered={false}
                        dataSource={this.state.visiblePolicies}
                        rowKey={(row) => row.id}
                        onChange={this.handleChange}
                        pagination={false}
                        footer={(currentTableData) => (
                            <SwSimpleLinkPagination
                                currentTableData={currentTableData}
                                links={this.state.paginationLinks}
                                onPrev={(_ev, _link, config) => {
                                    this.setState({ page: config.current! }, () => this.fetchAccessPolicies())
                                }}
                                onNext={(_ev, _link, config) => {
                                    this.setState({ page: config.current! }, () => this.fetchAccessPolicies())
                                }}
                            />
                        )}
                        loading={
                            this.isLoading && {
                                indicator: <Loading3QuartersOutlined spin={true} />,
                            }
                        }
                        expandedRowRender={(policy) => <AccessPolicyExpandedRowView policy={policy} />}
                    >
                        <Table.Column
                            className="name"
                            title="Name"
                            dataIndex="name"
                            render={this.renderDisplay}
                        />
                        <Table.Column
                            className="active"
                            title="Enabled"
                            dataIndex="is_active"
                            align="center"
                            render={TableRowActiveBadge}
                        />
                        <Table.Column
                            className="actions"
                            title="Actions"
                            align="center"
                            render={(_, policy: IAccessPolicy) => this.renderRowActions(policy)}
                        />
                    </Table>
                </div>

                <AccessPolicyEditor
                    accessPolicy={this.newPolicyDefaults}
                    visible={this.state.newPolicyDrawerVisible}
                    onClose={this.hideNewPolicyDrawer}
                    onSubmit={this.handleNewPolicySubmit}
                />
            </>
        )
    }

    protected get isLoading(): boolean {
        return this.state.loading && !this.state.policiesLoaded
    }

    public get newPolicyDefaults(): IAccessPolicy {
        return {
            name: null,
            is_active: true,
            effect: 'allow',
        } as IAccessPolicy
    }

    protected renderDisplay = (_text: string, policy: IAccessPolicy): React.ReactNode => {
        return <TableRowEntityDisplay title={policy.name} status={policy.description} />
    }

    protected renderRowActions(policy: IAccessPolicy): React.ReactNode {
        const actions: ButtonAction[] = []

        if (policy.is_active) {
            actions.push({
                key: 'disable',
                text: 'Disable',
                icon: <EyeInvisibleOutlined />,
                onClick: () => this.handleActionClick('disable', policy, null as any),
            })
        } else {
            actions.push({
                key: 'enable',
                text: 'Enable',
                icon: <EyeOutlined />,
                onClick: () => this.handleActionClick('enable', policy, null as any),
            })
        }

        return (
            <Button
                size="small"
                altHref={this.buildViewPolicyUrl(policy)}
                onClick={(ev) => this.handlePrimaryActionClick(policy, ev)}
                actions={actions.length > 0 ? actions : undefined}
            >
                View
            </Button>
        )
    }

    protected buildViewPolicyUrl(policy: IAccessPolicy): string {
        return `/access-control/access-policies/${policy.id}`
    }

    protected showNewPolicyDrawer = () => {
        this.setState({ newPolicyDrawerVisible: true })
    }

    protected hideNewPolicyDrawer = () => {
        this.setState({ newPolicyDrawerVisible: false })
    }

    protected handleNewPolicySubmit = async (policy: IAccessPolicy, policyDefaults: IAccessPolicy): Promise<void> => {
        const showError = () =>
            simpleNotification('error', 'Cannot process new Access Policy at this time. Please try again later.')

        try {
            const res = await this.accessPolicyService.create({
                ...policyDefaults,
                ...policy,
            })

            if (res.ok) {
                this.hideNewPolicyDrawer()
                this.appService.route(this.buildViewPolicyUrl(res.data))
            } else if (isAxiosResponseError(res.error)) {
                simpleNotification('error', res.error.response.data.message ?? res.error.message)
            }
        } catch (_) {
            showError()
        }
    }

    protected handleChange = (
        _pagination: TablePaginationConfig,
        _filters: Record<string, FilterValue | null>,
        sorter: SorterResult<IAccessPolicy>,
        _extra: TableCurrentDataSource<IAccessPolicy>,
    ) => {
        this.setState({ sortedInfo: sorter })
        this.fetchAccessPolicies()
    }

    protected handlePrimaryActionClick(policy: IAccessPolicy, event: React.MouseEvent): void {
        if (event.metaKey) {
            window.open(this.buildViewPolicyUrl(policy))
        } else {
            this.appService.route(this.buildViewPolicyUrl(policy))
        }
    }

    protected handleActionClick = (
        key: string | number,
        policy: IAccessPolicy,
        _domEvent: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    ): void => {
        if (key === 'disable') {
            const formRef = React.createRef<FormInstance>()

            Modal.confirm({
                title: 'Disable User Role',
                icon: <WarningOutlined />,
                okText: 'Disable',
                onOk: async () => {
                    const form = formRef.current

                    if (form) {
                        await form.validateFields()
                        return this.handleDisable(policy)
                    }
                },
                content: (
                    <Form ref={formRef}>
                        <p>
                            Are you sure you want to disable this access policy? Any users currently assigned this
                            policy will be affected.
                        </p>

                        <p>
                            Please type, <b>{DISABLE_KEY}</b>, to continue with this action.
                        </p>

                        <Form.Item
                            name="name"
                            validateTrigger={false}
                            rules={[
                                {
                                    validator: (_rule: RuleObject, value, callback) => {
                                        if (!value?.trim()) {
                                            callback(`${DISABLE_KEY} keyword is required`)
                                        } else if (value !== 'DISABLE') {
                                            callback(`You must type ${DISABLE_KEY} to continue`)
                                        } else {
                                            callback()
                                        }
                                    },
                                },
                            ]}
                        >
                            <Input autoFocus={true} />
                        </Form.Item>
                    </Form>
                ),
            } as any)
        } else if (key === 'enable') {
            Modal.confirm({
                title: 'Enable Org',
                icon: <QuestionCircleOutlined />,
                okText: 'Enable',
                onOk: () => this.handleEnable(policy),
                content: (
                    <p>
                        Are you sure you want to enable this access policy? Any users currently assigned this policy
                        will be be affected.
                    </p>
                ),
            } as any)
        }
    }

    protected handleDisable = async (policy: IAccessPolicy) => {
        const res = await this.accessPolicyService.patch(policy.id, {
            is_active: false,
        })

        if (res.ok) {
            await this.fetchAccessPolicies()
        } else if (isAxiosResponseError(res.error)) {
            simpleNotification('error', res.error.response.data.message ?? res.error.message)
        }
    }

    protected handleEnable = async (policy: IAccessPolicy) => {
        const res = await this.accessPolicyService.patch(policy.id, {
            is_active: true,
        })

        if (res.ok) {
            await this.fetchAccessPolicies()
        } else if (isAxiosResponseError(res.error)) {
            simpleNotification('error', res.error.response.data.message ?? res.error.message)
        }
    }

    protected searchUserRoles = async (data: string | any) => {
        const value = typeof data !== 'string' ? data.target.value : data

        if (!value || value.length === 0 || value.length >= 2) {
            await this.setState({ page: 1, query: value })
            await this.fetchAccessPolicies(value)
        }
    }

    protected async fetchAccessPolicies(search?: string) {
        this.setState({ loading: true, policiesLoaded: false })
        const update: Partial<IListState> = {
            loading: false,
        }

        try {
            const { data, meta } =
                (await this.accessPolicyService.fetchAll({
                    query: {
                        limit: this.state.pageSize,
                        page: this.state.page,
                        search: search ? search : this.state.query,
                        pagination: 1,
                    },
                    showLoadingScreen: false,
                })) || {}

            const policies: IAccessPolicy[] = data.map((policy) => {
                return {
                    id: policy.id,
                    name: policy.name,
                    description: policy.description ?? null,
                    subjects: policy.subjects,
                    effect: policy.effect,
                    actions: policy.actions,
                    conditions: policy.conditions,
                    is_active: policy.is_active,
                    created_at: policy.created_at,
                    created_by: policy.created_by,
                    created_by_user_name: policy.created_by_user_name,
                }
            })

            update.policiesLoaded = true
            update.allPolicies = policies
            update.visiblePolicies = policies
            update.tableMeta = meta
            update.paginationLinks = meta.links ?? update.paginationLinks
        } catch (error) {
            console.warn(error)
        }

        this.setState(update as IListState)
    }
}

export default AccessPolicyList
